import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useNotify } from "../../context/NotificationContext";
import { Card, Form, Input, Select, Button, Space, Row, Col, Typography, Badge, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined, SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import masterService from "../../services/masterService";
import ImageUpload from "../../components/banners/ImageUpload";
import { noWhitespaceValidator } from "../../utils/commonFunctions";
import { useCommon } from '../../context/CommonContext';
const { Title, Text } = Typography;

const { Option } = Select;

const defaultFormState = {
  mob_id: null,
  mob_name: null,
  mob_heading: null,
  mob_text: null,
  mob_order: null,
  images_arr: [],
  is_active: "Y",
};

const OnBoardingForm = () => {
  const navigate = useNavigate();
  const { mob_id } = useParams();
  const isEditMode = useMemo(() => mob_id && !isNaN(Number(mob_id)), [mob_id]);
  const notify = useNotify();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [formData, setFormData] = useState(defaultFormState);
  const { setPageTitle } = useCommon();
  useEffect(() => {
    setPageTitle(isEditMode ? "Edit OnBoarding" : "Add OnBoarding");
  }, []);
  // Load languages
  const loadLanguages = useCallback(async () => {
    try {
      const res = await masterService.getVideoLanguages({
        isAll: true,
        status: "Y",
      });
      if (res?.data?.status) {
        const payload = res.data.data;
        const list = Array.isArray(payload) ? payload : payload?.data || [];
        setLanguages(list || []);
      }
    } catch (e) {
      console.error("Error loading languages:", e);
      notify.error({
        message: "Failed to load languages",
      });
    }
  }, []);

  // Load details in edit mode
  const loadDetails = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const res = await masterService.getSingleOnBoarding(id);
        if (res?.data?.status) {
          const data = res.data.data || {};
          // Ensure images array exists
          const normalized = {
            mob_id: data.mob_id ?? null,
            mob_name: data.mob_name ?? null,
            mob_heading: data.mob_heading ?? null,
            mob_text: data.mob_text ?? null,
            mob_order: data.mob_order ?? null,
            images_arr: Array.isArray(data.images_arr) ? data.images_arr : [],
            is_active: data.is_active ?? "Y",
          };
          setFormData(normalized);
          form.setFieldsValue(normalized);
        }
      } catch (e) {
        console.error("Error loading onboarding details:", e);
        notify.error({
          message: "Failed to load onboarding details",
        });
      } finally {
        setLoading(false);
      }
    },
    [form]
  );

  useEffect(() => {
    loadLanguages();
    if (isEditMode) {
      loadDetails(mob_id);
    } else {
      // initialize defaults
      form.setFieldsValue(defaultFormState);
    }
  }, [isEditMode, mob_id, form, loadLanguages, loadDetails]);

  // Add image row
  const handleAddImage = () => {
    const next = {
      ...formData,
      images_arr: [
        ...formData.images_arr,
        { lang_id: null, card_image: null, banner_image: null },
      ],
    };
    setFormData(next);
    form.setFieldsValue(next);
  };

  // Remove image row
  const handleRemoveImage = (index) => {
    const copy = [...formData.images_arr];
    copy.splice(index, 1);
    const next = { ...formData, images_arr: copy };
    setFormData(next);
    form.setFieldsValue(next);
  };

  // Constrain order input to numbers only (similar to onlyNumber in Vue)
  const allowOnlyNumber = (event) => {
    const charCode = event.which ? event.which : event.keyCode;
    const allowedControl = [8, 46, 37, 39];
    const isDigit = charCode >= 48 && charCode <= 57;
    if (!isDigit && !allowedControl.includes(charCode)) {
      event.preventDefault();
    }
  };

  const handleReset = () => {
    setFormData(defaultFormState);
    form.resetFields();
  };

  const handleBack = () => navigate(-1);

  const handleFinish = async (values) => {
    try {
      setSaving(true);
      const payload = {
        ...formData,
        ...values,
      };
      let res;
      if (isEditMode) {
        res = await masterService.updateOnBoarding(payload, mob_id);
      } else {
        res = await masterService.saveOnBoarding(payload);
      }
      if (res?.data?.status) {
        notify.success({ message: res.data.message || "Success" });
        handleReset();
        handleBack();
      } else {
        notify.error({ message: res?.data?.message || "Not Saved" });
      }
    } catch (e) {
      notify.error({ message: "Server Error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="">
      <div className="">
        {/* Header Section */}

        <Card
          loading={loading}
          className="shadow-lg border-0"
        >
          <Form
            form={form}
            layout="vertical"
            size="large"
            initialValues={formData}
            onValuesChange={(_, allValues) =>
              setFormData((prev) => ({ ...prev, ...allValues }))
            }
            onFinish={handleFinish}
            className="space-y-6"
          >
            {/* Basic Information Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Basic Information
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label="Name"
                    name="mob_name"
                    rules={[
                      { required: true, message: "Please enter a name" },
                      { validator: noWhitespaceValidator },
                    ]}
                  >
                    <Input
                      placeholder="Enter onboarding name"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Heading
                      </span>
                    }
                    name="mob_heading"
                    rules={[
                      { required: true, message: "Please enter a Heading" },
                      { validator: noWhitespaceValidator }]}
                  >
                    <Input
                      placeholder="Enter heading text"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Order
                      </span>
                    }
                    name="mob_order"
                    rules={[
                      {
                        pattern: /^(0|[1-9]\d*)$/,
                        message: "Order must be a non-negative integer",
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      onKeyDown={allowOnlyNumber}
                      placeholder="Enter display order"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Description
                      </span>
                    }
                    name="mob_text"
                    rules={[
                      { required: true, message: "Please enter a Description" },
                      { validator: noWhitespaceValidator }]}
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder="Enter description text"
                      className="rounded-lg"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Media Assets Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Title
                    level={4}
                    className="!mb-0"
                    style={{ color: "rgba(0, 0, 0, 0.88)" }}
                  >
                    Media Assets
                  </Title>
                  <Badge
                    count={formData.images_arr?.length || 0}
                  />
                </div>
                <Button
                  type="primary"
                  onClick={handleAddImage}
                  icon={<PlusOutlined />}
                >
                  Add Image Set
                </Button>
              </div>

              {formData.images_arr?.map((image, index) => (
                <Card
                  key={`image-${index}`}
                  className="mb-6 border border-gray-200 bg-white"
                  style={{ borderColor: "#d9d9d9" }}
                  title={
                    <div className="flex items-center justify-between">
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Image Set #{index + 1}
                      </span>
                      <Tooltip title="Remove this image set">
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveImage(index)}
                          className="hover:scale-105 transition-transform"
                        >
                          Remove
                        </Button>
                      </Tooltip>
                    </div>
                  }
                >
                  <Row gutter={[24, 16]}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label={
                          <span
                            className="font-medium"
                            style={{ color: "rgba(0, 0, 0, 0.88)" }}
                          >
                            Language
                          </span>
                        }
                        name={["images_arr", index, "lang_id"]}
                      >
                        <Select
                          placeholder="Select language"
                          showSearch
                          optionFilterProp="children"
                          className="rounded-lg"
                        >
                          {languages.map((obj) => (
                            <Option key={obj.lang_id} value={obj.lang_id}>
                              {obj.lang_name}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[24, 16]}>
                    <Col xs={24} sm={12}>
                      <ImageUpload
                        label="Card Image (800x1200)"
                        name={["images_arr", index, "card_image"]}
                        imageType="video"
                        width={800}
                        height={1200}
                        value={image.card_image}
                        onChange={(val) => {
                          const copy = [...(formData.images_arr || [])];
                          copy[index] = { ...copy[index], card_image: val };
                          const next = { ...formData, images_arr: copy };
                          setFormData(next);
                          form.setFieldsValue(next);
                        }}
                      />
                    </Col>

                    <Col xs={24} sm={12}>
                      <ImageUpload
                        label="Banner Image (1280x720)"
                        name={["images_arr", index, "banner_image"]}
                        width={1280}
                        height={720}
                        imageType="video"
                        value={image.banner_image}
                        onChange={(val) => {
                          const copy = [...(formData.images_arr || [])];
                          copy[index] = { ...copy[index], banner_image: val };
                          const next = { ...formData, images_arr: copy };
                          setFormData(next);
                          form.setFieldsValue(next);
                        }}
                      />
                    </Col>
                  </Row>
                </Card>
              ))}

              {(!formData.images_arr || formData.images_arr.length === 0) && (
                <div
                  className="text-center py-12 bg-white rounded-lg border-2 border-dashed"
                  style={{ borderColor: "#d9d9d9" }}
                >
                  <div className="text-4xl mb-4">
                    <PlusOutlined />
                  </div>
                  <Text
                    type="secondary"
                    className="text-lg"
                    style={{ color: "rgba(0, 0, 0, 0.65)" }}
                  >
                    No media assets added yet
                  </Text>
                  <br />
                  <Text
                    type="secondary"
                    style={{ color: "rgba(0, 0, 0, 0.65)" }}
                  >
                    Click "Add Image Set" to get started
                  </Text>
                </div>
              )}
            </div>

            {/* Status Section */}
            <div
              className="bg-white p-6 rounded-lg border border-gray-200"
              style={{ backgroundColor: "#ffffff", borderColor: "#d9d9d9" }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Title
                  level={4}
                  className="!mb-0"
                  style={{ color: "rgba(0, 0, 0, 0.88)" }}
                >
                  Status & Configuration
                </Title>
              </div>

              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} lg={8}>
                  <Form.Item
                    label={
                      <span
                        className="font-semibold"
                        style={{ color: "rgba(0, 0, 0, 0.88)" }}
                      >
                        Status
                      </span>
                    }
                    name="is_active"
                  >
                    <Select className="rounded-lg">
                      <Option value="Y">
                        <Badge status="success" text="Active" />
                      </Option>
                      <Option value="N">
                        <Badge status="default" text="Inactive" />
                      </Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Action Buttons */}
            <div
              className="flex justify-end gap-4 pt-6 border-t"
              style={{ borderColor: "#d9d9d9" }}
            >
              <Space size="middle">
                <Button
                  onClick={handleReset}
                  icon={<ReloadOutlined />}
                  size="large"
                  className="px-6"
                >
                  Reset
                </Button>
                <Button
                  onClick={handleBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  icon={<SaveOutlined />}
                  size="large"
                  className="px-8"
                >
                  {saving ? "Saving..." : "Save Onboarding"}
                </Button>
              </Space>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default OnBoardingForm;
